<template>
  <div class="p-6 space-y-6">
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-bold flex items-center gap-2">
        Session Overview
        <BaseInfoIcon :text="ERKLAERUNG.seitenUebersichtProfessor" label="Session Overview" />
      </h1>
      <div class="flex items-center gap-2">
        <BaseButton
          variant="switch"
          :disabled="savingWeights || !hasWeightChanges"
          @click="saveAllWeights"
        >
          {{ savingWeights ? "Saving..." : "Save Weights" }}
        </BaseButton>
        <BaseInfoIcon :text="ERKLAERUNG.gewichteSpeichern" label="Save Weights" />
      </div>
    </div>

    <div class="bg-white rounded shadow overflow-hidden">
      <div class="grid grid-cols-[auto_1fr_auto]">
        <!-- Left Column: Student Names -->
        <div class="border-r">
          <!-- Header -->
          <div class="px-4 py-3 text-sm font-medium border-b bg-gray-100 min-w-[180px] min-h-[106px] flex items-center">Student</div>
          <!-- Rows -->
          <div v-for="student in students" :key="`student-${student.user_id}`" class="px-4 py-3 text-sm font-medium border-b flex items-center min-h-[110px]">
            {{ student.last_name }}, {{ student.first_name }}
          </div>
        </div>

        <!-- Middle Column: Sessions (Scrollable) -->
        <div class="overflow-x-auto bg-gray-50">
          <!-- Header -->
          <div class="flex border-b bg-gray-100 min-h-[106px]">
            <div
              v-for="session in sessions"
              :key="`session-header-${session.session_id}`"
              class="px-4 py-3 text-sm min-w-[180px] border-r last:border-r-0 bg-gray-100"
            >
              <div class="font-semibold mb-2">{{ session.title }}</div>
              <label class="text-xs text-gray-500 mb-1 flex items-center gap-1">
                Weight
                <BaseInfoIcon :text="ERKLAERUNG.gewicht" label="Weight" fixiert />
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                class="w-full border rounded px-2 py-1"
                v-model="sessionWeightInputs[session.session_id]"
              />
            </div>
          </div>
          <!-- Rows -->
          <div v-for="student in students" :key="`grades-${student.user_id}`" class="flex border-b min-h-[110px] bg-white">
            <div
              v-for="grade in student.session_grades"
              :key="`grade-${student.user_id}-${grade.session_id}`"
              class="px-4 py-3 text-sm min-w-[180px] border-r last:border-r-0 flex items-center bg-white"
            >
              {{ formatGrade(grade.grade) }}
            </div>
          </div>
        </div>

        <!-- Right Column: Actions -->
        <div class="border-l">
          <!-- Header -->
          <div class="grid grid-cols-3 border-b bg-gray-100 min-h-[106px]">
            <div class="px-4 py-3 text-sm font-medium border-r min-w-[220px] flex items-center gap-1">
              Overall Grade
              <BaseInfoIcon :text="ERKLAERUNG.gesamtnote" label="Overall Grade" />
            </div>
            <div class="px-4 py-3 text-sm font-medium border-r min-w-[100px] flex items-center gap-1">
              Release
              <BaseInfoIcon :text="ERKLAERUNG.freigabe" label="Release" />
            </div>
            <div class="px-4 py-3 text-sm font-medium min-w-[100px] flex items-center">Action</div>
          </div>
          <!-- Rows -->
          <div v-for="student in students" :key="`actions-${student.user_id}`" class="grid grid-cols-3 border-b min-h-[110px]">
            <div class="px-4 py-3 text-sm border-r flex items-center">
              <div class="w-full">
                <select
                  class="w-full border rounded px-2 py-1"
                  v-model="overallGradeInputs[student.user_id]"
                >
                  <option value="">-- Select Grade --</option>
                  <option
                    v-for="gradeOption in allowedOverallGrades"
                    :key="`overall-grade-${gradeOption}`"
                    :value="String(gradeOption)"
                  >
                    {{ Number(gradeOption).toFixed(1) }}
                  </option>
                </select>
                <div class="mt-2">
                  <span class="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    Auto {{ formatGrade(student.calculated_overall_grade) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="px-4 py-3 text-sm border-r flex items-center">
              <BaseToggle
                v-model="overallGradeReleaseInputs[student.user_id]"
                :disabled="savingStudentId === student.user_id"
                :label="overallGradeReleaseInputs[student.user_id] ? 'Released' : 'Draft'"
                @update:modelValue="onOverallGradeReleaseToggle(student.user_id, $event)"
              />
            </div>
            <div class="px-4 py-3 text-sm flex items-center">
              <div class="w-full">
                <BaseButton
                  class="px-3 py-1"
                  :disabled="savingStudentId === student.user_id || !hasSavableOverallGradeChanges(student.user_id)"
                  @click="saveOverallGrade(student.user_id)"
                >
                  {{ savingStudentId === student.user_id ? "..." : "Save" }}
                </BaseButton>
                <div v-if="isAutomaticOverallGrade(student.user_id)" class="mt-2">
                  <span class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                    Automatic
                  </span>
                </div>
                <div v-else class="mt-2">
                  <span class="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                    Manual
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded shadow p-4">
      <h2 class="text-lg font-semibold mb-3">Session Feedback Details</h2>
      <div class="space-y-3">
        <div
          v-for="session in sessions"
          :key="`feedback-${session.session_id}`"
          class="border rounded"
        >
          <button
            class="w-full px-4 py-3 text-left font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            @click="toggleSessionDetails(session.session_id)"
          >
            <ChevronDownIcon v-if="expandedSessionIds[session.session_id]" class="w-5 h-5 flex-shrink-0" />
            <ChevronRightIcon v-else class="w-5 h-5 flex-shrink-0" />
            <span>{{ session.title }}</span>
          </button>

          <div v-if="expandedSessionIds[session.session_id]" class="overflow-x-auto border-t">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-100">
                <tr>
                  <th class="text-left px-4 py-2 min-w-[180px] sticky left-0 bg-gray-100 z-10 border-r border-gray-300">Student</th>
                  <th class="text-left px-4 py-2 min-w-[80px]">Grade</th>
                  <th class="text-left px-4 py-2 min-w-[300px]">Evaluation</th>
                  <th class="text-left px-4 py-2 min-w-[300px]">Recommendation</th>
                  <th class="text-left px-4 py-2 min-w-[250px]">Goal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="student in students" :key="`feedback-row-${session.session_id}-${student.user_id}`" class="border-t align-top">
                  <td class="px-4 py-2 font-medium sticky left-0 bg-white z-10 border-r border-gray-200">{{ student.last_name }}, {{ student.first_name }}</td>
                  <td class="px-4 py-2">{{ formatGrade(getSessionFeedback(student, session.session_id).grade) }}</td>
                  <td class="px-4 py-2 whitespace-pre-wrap">{{ getSessionFeedback(student, session.session_id).evaluation || "-" }}</td>
                  <td class="px-4 py-2 whitespace-pre-wrap">{{ getSessionFeedback(student, session.session_id).recommendation || "-" }}</td>
                  <td class="px-4 py-2 whitespace-pre-wrap">{{ getSessionFeedback(student, session.session_id).goal || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <p v-if="errorMessage" class="text-red-600">{{ errorMessage }}</p>
  </div>
</template>

<script>
import { getProfessorSessionOverview } from "@/live-sessions/api/usersessions";
import { getSession, updateSession } from "@/live-sessions/api/sessions";
import { updateUserOverallGrade } from "@/api/users";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import BaseToggle from "@/BaseComponents/BaseToggle.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";

export default {
  name: "ProfessorSessionOverview",
  components: {
    BaseButton,
    BaseInfoIcon,
    BaseToggle,
    ChevronDownIcon,
    ChevronRightIcon,
  },
  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      students: [],
      allowedOverallGrades: [1.0, 1.3, 1.7, 2.0, 2.3, 2.5, 2.7, 3.0, 3.3, 3.7, 4.0, 5.0],
      sessionWeightInputs: {},
      originalSessionWeights: {},
      overallGradeInputs: {},
      overallGradeReleaseInputs: {},
      originalOverallGradeInputs: {},
      originalOverallGradeReleaseInputs: {},
      overallGradeIsAutomatic: {},
      savingWeights: false,
      savingStudentId: null,
      expandedSessionIds: {},
      errorMessage: "",
    };
  },
  computed: {
    hasWeightChanges() {
      return this.sessions.some((session) => {
        const sessionId = session.session_id;
        const currentWeight = this.normalizeWeight(this.sessionWeightInputs[sessionId]);
        const originalWeight = this.normalizeWeight(this.originalSessionWeights[sessionId]);

        if (currentWeight === null || originalWeight === null) {
          return false;
        }

        return currentWeight !== originalWeight;
      });
    },
  },
  async mounted() {
    await this.loadOverview();
  },
  methods: {
    normalizeWeight(value) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue) || numericValue < 0) {
        return null;
      }
      return Number(numericValue.toFixed(4));
    },
    formatGrade(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
      }
      return Number(value).toFixed(2);
    },
    isAllowedOverallGrade(value) {
      return this.allowedOverallGrades.includes(Number(value));
    },
    isAutomaticOverallGrade(userId) {
      return Boolean(this.overallGradeIsAutomatic[userId]);
    },
    hasSavableOverallGradeChanges(userId) {
      const currentGrade = this.overallGradeInputs[userId] ?? "";
      const originalGrade = this.originalOverallGradeInputs[userId] ?? "";
      const currentRelease = Boolean(this.overallGradeReleaseInputs[userId]);
      const originalRelease = Boolean(this.originalOverallGradeReleaseInputs[userId]);

      return currentGrade !== originalGrade || currentRelease !== originalRelease;
    },
    getNearestAllowedOverallGrade(value) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }

      return this.allowedOverallGrades.reduce((closest, current) => {
        if (closest === null) {
          return current;
        }

        const currentDistance = Math.abs(current - numericValue);
        const closestDistance = Math.abs(closest - numericValue);
        return currentDistance < closestDistance ? current : closest;
      }, null);
    },
    async loadOverview() {
      this.errorMessage = "";
      try {
        const { data } = await getProfessorSessionOverview();
        this.sessions = data.sessions || [];
        this.students = data.students || [];

        const sessionWeights = {};
        this.sessions.forEach((session) => {
          sessionWeights[session.session_id] = String(session.weight ?? 1);
        });
        this.sessionWeightInputs = sessionWeights;
        this.originalSessionWeights = { ...sessionWeights };

        const grades = {};
        const releases = {};
        const automaticFlags = {};
        this.students.forEach((student) => {
          const hasManualOverall = student.overall_grade !== null && student.overall_grade !== undefined;
          let selectedOverall = "";

          if (hasManualOverall) {
            if (this.isAllowedOverallGrade(student.overall_grade)) {
              selectedOverall = String(student.overall_grade);
            } else {
              const nearestManualOverall = this.getNearestAllowedOverallGrade(student.overall_grade);
              selectedOverall = nearestManualOverall !== null ? String(nearestManualOverall) : "";
            }
          } else if (student.calculated_overall_grade !== null && student.calculated_overall_grade !== undefined) {
            const nearestCalculatedOverall = this.getNearestAllowedOverallGrade(student.calculated_overall_grade);
            selectedOverall = nearestCalculatedOverall !== null ? String(nearestCalculatedOverall) : "";
          }

          grades[student.user_id] = selectedOverall;
          releases[student.user_id] = Boolean(student.overall_grade_released);
          automaticFlags[student.user_id] = !hasManualOverall;
        });
        this.overallGradeInputs = grades;
        this.overallGradeReleaseInputs = releases;
        this.originalOverallGradeInputs = { ...grades };
        this.originalOverallGradeReleaseInputs = { ...releases };
        this.overallGradeIsAutomatic = automaticFlags;
      } catch (error) {
        console.error(error);
        this.errorMessage = "Session overview could not be loaded.";
      }
    },
    toggleSessionDetails(sessionId) {
      this.expandedSessionIds = {
        ...this.expandedSessionIds,
        [sessionId]: !this.expandedSessionIds[sessionId],
      };
    },
    getSessionFeedback(student, sessionId) {
      return (student.session_grades || []).find((entry) => entry.session_id === sessionId) || {};
    },
    async saveAllWeights() {
      this.savingWeights = true;
      this.errorMessage = "";

      try {
        for (const session of this.sessions) {
          const rawWeight = this.sessionWeightInputs[session.session_id];
          const parsedWeight = Number(rawWeight);
          if (Number.isNaN(parsedWeight) || parsedWeight < 0) {
            continue;
          }

          const { data: sessionDetail } = await getSession(session.session_id);
          const payload = {
            title: sessionDetail.title,
            description: sessionDetail.description,
            parent_id: sessionDetail.parent_id,
            start_date: sessionDetail.start_date,
            end_date: sessionDetail.end_date,
            weight: parsedWeight,
            is_async: sessionDetail.is_async,
            ended: sessionDetail.ended,
            criteria: (sessionDetail.criteria || []).map((entry) => ({
              id: entry.criterion.id,
              role_id: entry.role_id,
              weight: entry.weight,
            })),
          };

          await updateSession(session.session_id, payload);
        }

        await this.loadOverview();
      } catch (error) {
        console.error(error);
        this.errorMessage = "Weights could not be saved.";
      } finally {
        this.savingWeights = false;
      }
    },
    async saveOverallGrade(userId) {
      this.savingStudentId = userId;
      this.errorMessage = "";

      try {
        const raw = this.overallGradeInputs[userId];
        const parsed = raw === "" || raw === null || raw === undefined ? null : Number(raw);
        if (parsed !== null && (Number.isNaN(parsed) || !this.isAllowedOverallGrade(parsed))) {
          this.errorMessage = "Overall grade is invalid. Please select an allowed grade.";
          return;
        }

        await updateUserOverallGrade(userId, {
          overall_grade: parsed,
          overall_grade_released: Boolean(this.overallGradeReleaseInputs[userId]),
        });
        await this.loadOverview();
      } catch (error) {
        console.error(error);
        this.errorMessage = "Overall grade could not be saved.";
      } finally {
        this.savingStudentId = null;
      }
    },
    async onOverallGradeReleaseToggle(userId, nextValue) {
      this.overallGradeReleaseInputs = {
        ...this.overallGradeReleaseInputs,
        [userId]: Boolean(nextValue),
      };

      if (this.savingStudentId === userId || !this.hasSavableOverallGradeChanges(userId)) {
        return;
      }

      await this.saveOverallGrade(userId);
    },
  },
};
</script>
